import { asArray, asBoolean, asDate, asNumber, asRaw, asString } from './guards'

const IMAGE_BASE_URL = 'https://cdn.discordapp.com/'
const GUILD_ICON_PATH = 'icons/{guild_id}/{guild_icon}'

/**
 * Port of constructors/discord_guild.js.
 *
 * `DiscordGuildRegister` is a guild as the JLM API knows it (registered with
 * the bot, with member counts); `DiscordGuild` is the raw shape Discord's own
 * /users/@me/guilds returns.
 */
export class DiscordGuildRegister {
  id: number | null
  discord_guild_uuid: string | null
  discord_channel_uuid: string | null
  discord_role_uuid: string | null
  created_at: Date | null
  updated_at: Date | null
  name: string | null
  icon: string | null
  permissions: string | null
  is_registered: boolean
  online_presence_count: number | null
  total_member_count: number | null
  jlm_member_count: number | null

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.discord_guild_uuid = asString(d.discord_guild_uuid)
    this.discord_channel_uuid = asString(d.discord_channel_uuid)
    this.discord_role_uuid = asString(d.discord_role_uuid)
    this.created_at = asDate(d.created_at)
    this.updated_at = asDate(d.updated_at)
    this.name = asString(d.name)
    this.icon = asString(d.icon)
    this.permissions = asString(d.permissions)
    this.is_registered = asBoolean(d.is_registered, false) as boolean
    this.online_presence_count = asNumber(d.online_presence_count)
    this.total_member_count = asNumber(d.total_member_count)
    this.jlm_member_count = asNumber(d.jlm_member_count)
  }

  /** Animated icons are prefixed "a_" and are served as .gif. */
  get icon_url(): string {
    if (!this.icon || !this.discord_guild_uuid) return ''
    const extension = this.icon.startsWith('a_') ? '.gif' : '.png'
    const path = GUILD_ICON_PATH.replace('{guild_id}', this.discord_guild_uuid).replace(
      '{guild_icon}',
      this.icon,
    )
    return `${IMAGE_BASE_URL}${path}${extension}`
  }

  get member_count(): number | null {
    return this.total_member_count
  }
}

export class DiscordGuild {
  id: string | null
  name: string | null
  icon: string | null
  permissions: string | null
  features: string[] | null
  is_registered: boolean
  is_managed: boolean

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asString(d.id)
    this.name = asString(d.name)
    this.icon = asString(d.icon)
    this.permissions = asString(d.permissions)
    this.features = asArray<string>(d.features)
    this.is_registered = asBoolean(d.is_registered, false) as boolean
    this.is_managed = asBoolean(d.is_managed, true) as boolean
  }
}

export function guildListFrom(data: unknown): DiscordGuildRegister[] {
  return (asArray(data) ?? []).map((item) => new DiscordGuildRegister(item))
}
